<template>
  <q-section v-if="resolvedActivityCode" class="ui-buff-card" v-bind="$attrs">
    <q-section bg-i="buff-icon" wh="100%" @click="openBuffModal"></q-section>
    <q-dialog :name="dialogName" bg-i="dialog-buff" pc="pc">
      <q-section wh="306 300" pt="56" class="flex flex-col items-center scroll-y gap-y-6 ui-buff-card__list">
        <q-section v-if="!buffList.length && !isBuffListLoading" f="14 #333 400" pc="x" pt="20">暂无 buff 卡</q-section>
        <q-section
          v-for="item in buffList"
          :key="item.buff_id"
          bg-i="buff-item"
          bg-s="contain"
          bg-p="center"
          bg-r="no-repeat"
          p="relative"
          class="flex flex-col justify-center items-start flex-noshrink">
          <q-section f="12 #333 400" class="ws-nowrap" p="relative" pl="60">
            {{ formatBuffLine(item) }}
          </q-section>
          <q-section v-if="item.buff_desc" f="10 #333 400" class="ws-nowrap" p="relative" pl="60">
            ({{ item.buff_desc }})
          </q-section>
          <q-section bg-i="to-use" pc="y" pl="187" @click="handleBuffClick(item)"></q-section>
        </q-section>
      </q-section>
    </q-dialog>
  </q-section>
</template>

<script setup lang="ts">
//统一 GET /api/activities/general/buff_cards；用卡 POST 必带 activity_code
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useDialogs } from '@/ui-runtime/core/composable/use-dialogs'
import { useToast } from '@/ui-runtime/core/composable/use-toast'
import { getBuffCards, postUseBuffCard, GENERAL_BUFF_CARDS_QUERY_KEY, type UiBuffCardItem } from './use-buff-card'

const props = withDefaults(
  defineProps<{
    /** 不传则从 route.meta.activity_code 读取 */
    activityCode?: string
    dialogName?: string
  }>(),
  {
    activityCode: '',
    dialogName: 'buffModal',
  }
)

const emit = defineEmits<{
  refetch: []
}>()

const route = useRoute()
const queryClient = useQueryClient()
const { $dialogs } = useDialogs()
const { $toast } = useToast()

const resolvedActivityCode = computed(() => {
  const fromProp = props.activityCode?.trim()
  if (fromProp) {
    return fromProp
  }
  return ((route.meta?.activity_code as string | undefined) || '').trim() || ''
})

const {
  data: buffQueryData,
  isFetching: isBuffListLoading,
  refetch: refetchBuffList,
} = useQuery({
  queryKey: [GENERAL_BUFF_CARDS_QUERY_KEY, resolvedActivityCode],
  queryFn: () => getBuffCards(resolvedActivityCode.value),
  enabled: () => !!resolvedActivityCode.value,
})

const buffList = computed<UiBuffCardItem[]>(() => buffQueryData.value?.data?.buff_cards || [])

const dialogCtl = computed(
  () => ($dialogs as Record<string, { open: () => void; close: () => void }> | undefined)?.[props.dialogName]
)

const formatBuffLine = (item: UiBuffCardItem) => {
  return `${item.buff_ratio}倍buff卡*${item.buff_duration}分钟`
}

const openBuffModal = () => {
  void refetchBuffList()
  dialogCtl.value?.open()
}

const handleBuffClick = async (item: UiBuffCardItem) => {
  const code = resolvedActivityCode.value
  if (!code) {
    $toast.show('缺少活动码')
    return
  }
  const res = await postUseBuffCard({
    buff_id: item.buff_id,
    activity_code: code,
  })
  if (res.status === 0) {
    await queryClient.invalidateQueries({ queryKey: [GENERAL_BUFF_CARDS_QUERY_KEY] })
    emit('refetch')
    dialogCtl.value?.close()
  } else {
    $toast.show((res as { msg?: string })?.msg || '')
  }
}
</script>

<style scoped lang="scss">
.ui-buff-card__list {
  box-sizing: border-box;
}
</style>
